import React, { useEffect } from 'react';
import Reveal from '../components/Reveal';
import { AutomationQuiz } from '../components/AutomationQuiz';
import { images, booking } from '../mock';
import { useBooking } from '../context/BookingContext';
import { ArrowRight, AppWindow, Globe, Workflow, ShieldCheck, Users } from 'lucide-react';

const SEO_TITLE = 'AI & Workflow Automation Consulting | The Answer Group Consulting';
const SEO_DESC = 'People-centered AI integration, workflow automation, custom applications, website development, and digital transformation consulting for organizations.';

const offerings = [
  { icon: AppWindow, title: 'Custom Applications & Digital Solutions', desc: 'Design and development of tailored web applications, internal tools, dashboards, and digital systems that address the unique needs of your organization.' },
  { icon: Globe, title: 'Website Design & Development', desc: "Purposeful, functional websites designed to strengthen your organization's digital presence, improve the user experience, and support your operational goals." },
  { icon: Workflow, title: 'Workflow Automation & Process Improvement', desc: 'Identify repetitive administrative tasks, workflow bottlenecks, and inefficient processes. Develop practical automations that reduce manual effort, improve coordination, and give teams more time for higher-value work.' },
  { icon: ShieldCheck, title: 'Responsible AI Integration & Adoption', desc: 'Help leaders and teams understand where artificial intelligence can add value, how to use it responsibly, and what safeguards and human oversight should remain in place.' },
  { icon: Users, title: 'Digital Transformation & Change Management', desc: 'Support organizations through the human side of technological change, including process redesign, employee engagement, training, implementation planning, and adoption.' },
];

const useSeo = () => {
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    const prevTitle = document.title;
    const prevDesc = meta?.getAttribute('content');
    document.title = SEO_TITLE;
    meta?.setAttribute('content', SEO_DESC);
    return () => { document.title = prevTitle; if (meta && prevDesc) meta.setAttribute('content', prevDesc); };
  }, []);
};

const BookButton = ({ testId, variant = 'btn-ivory', label = 'TAG Us In' }) => {
  const { openBooking } = useBooking();
  return (
    <button data-testid={testId} onClick={() => openBooking(booking.alignmentLab, 'Book a Session')} className={`${variant} rounded-full px-9 py-4 text-sm tracking-luxe uppercase inline-flex items-center gap-2`}>
      {label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
};

const Hero = () => (
  <section className="relative pt-40 pb-28" data-testid="ai-hero">
    <div className="absolute inset-0">
      <img src={images.aiHero} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#241033]/95 via-[#2C1743]/88 to-[#43265E]/65" />
    </div>
    <div className="relative max-w-[1280px] mx-auto px-6">
      <Reveal>
        <div className="eyebrow text-silver mb-5">Intelligent Systems & Process Automation</div>
        <h1 data-testid="ai-hero-title" className="font-display text-5xl md:text-6xl text-white font-medium leading-tight max-w-3xl mb-6">
          Automate the Process. Elevate the People.
        </h1>
        <p className="text-[#D9CEE6] font-light text-lg max-w-2xl mb-10">
          Technology should make work better for people, not simply require fewer of them.
        </p>
        <BookButton testId="ai-hero-book-btn" label="Book a Consultation" />
      </Reveal>
    </div>
  </section>
);

const Intro = () => (
  <section className="py-24 bg-[#FBFAF8]" data-testid="ai-intro">
    <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <p className="font-display text-3xl md:text-4xl text-[#241033] leading-snug mb-8">
          Every organization has processes that consume valuable time, create unnecessary bottlenecks, and pull talented people away from the work they were actually hired to do.
        </p>
        <p className="font-display italic text-2xl text-[#7E5C9E] mb-8">We believe there is a better way.</p>
        <div className="space-y-5 text-[#4A4453] font-light text-lg leading-relaxed">
          <p>At The Answer Group Consulting, we combine organizational psychology, process improvement, people-centered design, and emerging technology to help organizations rethink how work gets done.</p>
          <p>Our approach begins with understanding your people, operations, and organizational needs. From there, we identify opportunities to simplify workflows, automate repetitive tasks, and develop digital solutions that support greater effectiveness, consistency, and operational efficiency.</p>
          <p>Whether your organization needs a customized scheduling application, a more functional website, automated administrative workflows, or guidance on integrating artificial intelligence into daily operations, we help bridge the gap between identifying a problem and implementing a practical solution.</p>
          <p>And because technology is only as effective as the people and processes surrounding it, we prioritize usability, organizational readiness, responsible implementation, and sustainable adoption.</p>
        </div>
      </Reveal>
      <Reveal delay={150}>
        <div className="relative">
          <div className="absolute -inset-3 bg-silver rounded-sm opacity-50" />
          <img src={images.aiWorking} alt="Professional working on a laptop" className="relative w-full h-[560px] object-cover object-top rounded-sm" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Offerings = () => (
  <section className="py-28 bg-[#F4F1EC]" data-testid="ai-offerings">
    <div className="max-w-[1280px] mx-auto px-6">
      <Reveal>
        <div className="eyebrow text-[#7E5C9E] mb-4">Services</div>
        <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-16">What We Can Help You Build</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {offerings.map(({ icon: Icon, title, desc }, i) => (
          <Reveal key={title} delay={i * 80}>
            <div data-testid={`ai-offering-${i}`} className="h-full bg-white rounded-sm p-9 border border-[#E7E2EE] transition-transform duration-500 hover:-translate-y-1 shadow-[0_30px_60px_-45px_rgba(36,16,51,0.45)]">
              <div className="w-12 h-12 rounded-full bg-[#EDE8F2] flex items-center justify-center mb-6">
                <Icon className="w-5 h-5 text-[#43265E]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-2xl text-[#241033] font-medium mb-4">{title}</h3>
              <p className="text-[#4A4453] font-light leading-relaxed">{desc}</p>
            </div>
          </Reveal>
        ))}
        <Reveal delay={400}>
          <div className="h-full bg-[#43265E] rounded-sm p-9 flex flex-col justify-between">
            <p className="font-display text-2xl text-white leading-snug mb-8">Not sure where to start? We begin by understanding the problem.</p>
            <BookButton testId="ai-offerings-book-btn" label="Start a Conversation" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Approach = () => (
  <section className="py-28 bg-[#FBFAF8]" data-testid="ai-approach">
    <div className="max-w-[900px] mx-auto px-6">
      <Reveal>
        <div className="eyebrow text-[#7E5C9E] mb-4">Our Approach</div>
        <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-10">People First. Technology with Purpose.</h2>
        <div className="space-y-6 text-[#4A4453] font-light text-lg leading-relaxed">
          <p>We do not believe every organizational challenge requires a new application, and we do not believe every process should be automated.</p>
          <p className="font-display text-2xl text-[#43265E] italic">We begin by understanding the problem.</p>
          <p>We examine the work, the people performing it, and the systems supporting it. Then, we determine which processes should be simplified, improved, automated, or left in human hands.</p>
          <p>The goal is not technology for technology's sake.</p>
          <p className="border-l-2 border-[#7E5C9E] pl-6 text-[#241033]">The goal is to build practical systems that free your people to do more of the work that requires their creativity, judgment, expertise, and humanity.</p>
        </div>
      </Reveal>
    </div>
  </section>
);

const Closing = () => (
  <section className="py-28 bg-[#43265E]" data-testid="ai-closing">
    <div className="max-w-[900px] mx-auto px-6 text-center">
      <Reveal>
        <h2 className="font-display text-4xl md:text-5xl text-white font-medium leading-tight mb-6">Create Capacity for What Matters.</h2>
        <p className="text-[#E7E2EE] font-light text-lg leading-relaxed mb-6">
          Imagine what your organization could accomplish if your team spent less time managing repetitive tasks and more time innovating, collaborating, developing ideas, and delivering meaningful results.
        </p>
        <p className="text-[#D9CEE6] font-light text-lg mb-10">That is the opportunity we help you explore.</p>
        <p className="font-display italic text-2xl text-white mb-10">Let's rethink how work gets done.</p>
        <BookButton testId="ai-closing-book-btn" />
      </Reveal>
    </div>
  </section>
);

const AIWorkflowAutomation = () => {
  useSeo();
  return (
    <main data-testid="ai-workflow-page">
      <Hero />
      <Intro />
      <Offerings />
      <Approach />
      <AutomationQuiz />
      <Closing />
    </main>
  );
};

export default AIWorkflowAutomation;
